import { logger } from "../../utils/logger";
import React, { useState, useEffect } from 'react';
import { 
  Container, Typography, Box, Button, Paper, Chip, CircularProgress, 
  Alert, Snackbar, Tabs, Tab, Card, CardContent
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { 
  Refresh as RefreshIcon, 
  Calculate as CalculateIcon 
} from '@mui/icons-material';
import platformService from '../../services/platformService';
import { useParams, useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components';

const PlatformTransactions = () => {
  const { integrationId } = useParams();
  const [integration, setIntegration] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch integration details
        const integrationData = await platformService.getIntegration(integrationId);
        setIntegration(integrationData);
        
        // Fetch transactions
        await refreshTransactions();
      } catch (err) {
        setError('Failed to load data. Please try again.');
        logger.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [integrationId]);

  const refreshTransactions = async () => {
    try {
      setLoading(true);
      const options = { limit: 100 };
      if (statusFilter !== "all") {
        options.status = statusFilter;
      }
      const transactionsData = await platformService.listTransactions(integrationId, options);
      setTransactions(transactionsData);
    } catch (err) {
      setError('Failed to refresh transactions. Please try again.');
      logger.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCalculateTax = async () => {
    try {
      setLoading(true);
      await platformService.calculateTax(integrationId, { background: true });
      setSuccess('Tax calculation started in background. It may take a few minutes to complete.');
      setTimeout(refreshTransactions, 2000);  // Refresh after a delay
    } catch (err) {
      setError('Failed to start tax calculation. Please try again.');
      logger.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (event, newValue) => {
    setStatusFilter(newValue);
    // Refresh transactions when status filter changes
    setTimeout(() => {
      refreshTransactions();
    }, 100);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatAddress = (address) => {
    if (!address) return "N/A";
    const parts = [
      address.address1,
      address.address2,
      address.city,
      address.state,
      address.postal_code,
      address.country
    ].filter(Boolean);
    return parts.join(", ");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  const getStatusChip = (status) => {
    let color = "default";
    let label = status;
    
    switch (status) {
      case "PENDING_TAX_CALCULATION":
        color = "warning";
        label = "Pending";
        break;
      case "TAX_CALCULATED":
        color = "success";
        label = "Calculated";
        break;
      case "TAX_CALCULATION_FAILED":
        color = "error";
        label = "Failed";
        break;
      case "IMPORTED":
        color = "info";
        label = "Imported";
        break;
      default:
        break;
    }
    
    return <Chip label={label} color={color} size="small" />;
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'platform_transaction_id', headerName: 'Platform ID', width: 150 },
    { field: 'transaction_type', headerName: 'Type', width: 100 },
    { 
      field: 'transaction_date', 
      headerName: 'Date', 
      width: 180,
      valueGetter: (params) => formatDate(params.row.transaction_date)
    },
    { 
      field: 'amount', 
      headerName: 'Amount', 
      width: 120,
      valueGetter: (params) => formatCurrency(params.row.amount)
    },
    { 
      field: 'tax_amount', 
      headerName: 'Tax', 
      width: 120,
      valueGetter: (params) => formatCurrency(params.row.tax_amount)
    },
    { field: 'customer_name', headerName: 'Customer', width: 150 },
    { 
      field: 'status', 
      headerName: 'Status', 
      width: 150,
      renderCell: (params) => getStatusChip(params.row.status)
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      renderCell: (params) => (
        <Box>
          <Button
            size="small"
            onClick={() => { /* View transaction details */ }}
            color="primary"
          >
            View Details
          </Button>
        </Box>
      ),
    },
  ];

  // Helper to ensure error is always a string
  const getErrorMessage = (err) => {
    if (!err) return '';
    if (typeof err === 'string') return err;
    if (err instanceof Error) return err.message;
    if (err?.message) return String(err.message);
    return 'An error occurred';
  };

  return (
    <Container maxWidth="xl" className="gt-page gt-page-wide">
      <PageHeader 
        title="Platform Transactions" 
        breadcrumbs={[
          { label: 'Platform Integrations', path: '/platforms/integrations' },
          { label: 'Transactions', path: `/platforms/transactions/${integrationId}` }
        ]}
      />
      
      {integration && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Integration: {integration.platform ? integration.platform.name : "Unknown Platform"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Store ID: {integration.store_id}
            </Typography>
            {integration.last_sync_at && (
              <Typography variant="body2" color="text.secondary">
                Last Synced: {new Date(integration.last_sync_at).toLocaleString()}
              </Typography>
            )}
          </CardContent>
        </Card>
      )}
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Tabs value={statusFilter} onChange={handleStatusChange}>
          <Tab label="All" value="all" />
          <Tab label="Pending" value="PENDING_TAX_CALCULATION" />
          <Tab label="Calculated" value="TAX_CALCULATED" />
          <Tab label="Failed" value="TAX_CALCULATION_FAILED" />
          <Tab label="Imported" value="IMPORTED" />
        </Tabs>
        <Box>
          <Button 
            variant="contained" 
            startIcon={<CalculateIcon />} 
            onClick={handleCalculateTax}
            sx={{ mr: 1 }}
          >
            Calculate Tax
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<RefreshIcon />} 
            onClick={refreshTransactions}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      <Paper elevation={1}>
        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress /></Box>}
        
        <DataGrid
          rows={transactions}
          columns={columns}
          pageSize={50}
          rowsPerPageOptions={[25, 50, 100]}
          disableSelectionOnClick
          autoHeight
          loading={loading}
          sx={{ minHeight: 400 }}
        />
      </Paper>

      {/* Snackbars for success and error messages */}
      <Snackbar 
        open={!!success} 
        autoHideDuration={6000} 
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success">
          {success}
        </Alert>
      </Snackbar>

      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setError(null)} severity="error">
          {typeof error === 'string' ? error : getErrorMessage(error)}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PlatformTransactions; 